import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { AddressTagFilterProps } from './types';
import { filterTagsBySearch, getCollapsedSelectedTags } from './utils';

const useAddressTagFilterViewModel = ({
  isOpen,
  selectedTagIds,
  tags,
  texts,
  onOpenChange,
  onSelectedTagIdsChange,
}: AddressTagFilterProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [tagSearch, setTagSearch] = useState('');
  const [isAppliedTagsExpanded, setIsAppliedTagsExpanded] = useState(false);
  const [appliedTagsAvailableWidth, setAppliedTagsAvailableWidth] = useState(0);

  const selectedTagSet = useMemo(() => new Set(selectedTagIds), [selectedTagIds]);
  const selectedTags = useMemo(
    () => selectedTagIds.flatMap((tagId) => tags.find((tag) => tag.id === tagId) || []),
    [selectedTagIds, tags]
  );
  const filteredTags = useMemo(() => filterTagsBySearch(tags, tagSearch), [tagSearch, tags]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleDocumentPointerDown = (event: PointerEvent) => {
      const target = event.target;
      const isFilterControlTarget =
        target instanceof Element &&
        (filterButtonRef.current?.contains(target) || dropdownRef.current?.contains(target));

      if (!isFilterControlTarget) {
        onOpenChange(false);
      }
    };

    document.addEventListener('pointerdown', handleDocumentPointerDown);

    return () => document.removeEventListener('pointerdown', handleDocumentPointerDown);
  }, [isOpen, onOpenChange]);

  useLayoutEffect(() => {
    const containerElement = containerRef.current;

    if (!containerElement || selectedTags.length === 0) {
      setAppliedTagsAvailableWidth(0);

      return undefined;
    }

    const updateAppliedTagsWidth = () => {
      setAppliedTagsAvailableWidth(containerElement.getBoundingClientRect().width);
    };

    updateAppliedTagsWidth();

    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(updateAppliedTagsWidth);

    resizeObserver?.observe(containerElement);
    window.addEventListener('resize', updateAppliedTagsWidth);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateAppliedTagsWidth);
    };
  }, [selectedTags.length]);

  const collapsedSelectedTags = useMemo(
    () =>
      getCollapsedSelectedTags({
        availableWidth: appliedTagsAvailableWidth,
        selectedTags,
        texts,
      }),
    [appliedTagsAvailableWidth, selectedTags, texts]
  );
  const isAppliedTagsToggleVisible = collapsedSelectedTags.length < selectedTags.length;
  const visibleSelectedTags = isAppliedTagsExpanded ? selectedTags : collapsedSelectedTags;
  const hiddenSelectedTagsCount = Math.max(selectedTags.length - collapsedSelectedTags.length, 0);

  const onTagChange = useCallback(
    (tagId: string, isChecked: boolean) => {
      if (isChecked) {
        onSelectedTagIdsChange(selectedTagIds.includes(tagId) ? selectedTagIds : [...selectedTagIds, tagId]);

        return;
      }

      onSelectedTagIdsChange(selectedTagIds.filter((selectedTagId) => selectedTagId !== tagId));
    },
    [onSelectedTagIdsChange, selectedTagIds]
  );

  const onTagRemove = useCallback(
    (tagId: string) => {
      onSelectedTagIdsChange(selectedTagIds.filter((selectedTagId) => selectedTagId !== tagId));
    },
    [onSelectedTagIdsChange, selectedTagIds]
  );

  const onClear = useCallback(() => {
    setTagSearch('');
    setIsAppliedTagsExpanded(false);
    onSelectedTagIdsChange([]);
  }, [onSelectedTagIdsChange]);

  const onFilterButtonClick = useCallback(() => {
    onOpenChange(!isOpen);
  }, [isOpen, onOpenChange]);

  const onAppliedTagsToggle = useCallback(() => {
    setIsAppliedTagsExpanded((currentValue) => !currentValue);
  }, []);

  return {
    containerRef,
    dropdownRef,
    filterButtonRef,
    filteredTags,
    hiddenSelectedTagsCount,
    isAppliedTagsExpanded,
    isAppliedTagsToggleVisible,
    onAppliedTagsToggle,
    onClear,
    onFilterButtonClick,
    onTagChange,
    onTagRemove,
    selectedTagSet,
    selectedTags,
    setTagSearch,
    tagSearch,
    visibleSelectedTags,
  };
};

export default useAddressTagFilterViewModel;
